import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  first,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  tap,
} from 'rxjs';

import {
  CreateWorkflowVariableRequest,
  UpdateWorkflowVariableRequest,
  VariableInfo,
  WorkflowVariableDefinitionInfo,
} from '@blaze-shared/api-contracts-domain';
import {
  LocalStorageService,
  NotificationService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { isDeepEqual } from '@blaze-shared/shared-util';
import { normalizeVarName } from '@blaze-shared/variables-util';

import { VariablesCollectionService } from './api/variables-collection.service';
import { WorkflowVariablesApiService } from './api/workflow-variables-api.service';
import { VariablesStore } from './variables.store';

@Injectable({ providedIn: 'root' })
export class VariablesFacade {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly store = inject(VariablesStore);
  private readonly variablesCollService = inject(VariablesCollectionService);
  private readonly workflowVariablesApiService = inject(WorkflowVariablesApiService);

  private readonly localVarsChanged$ = new BehaviorSubject<VariableInfo[]>([]);
  private readonly isLoadingCounter = signal(0);

  private varChangesSub?: Subscription;
  private projectId?: string;

  readonly isLoading = computed(() => this.isLoadingCounter() > 0);
  readonly varDefs: Signal<WorkflowVariableDefinitionInfo[]> = this.store.varDefs;
  readonly varInfos: Signal<VariableInfo[]> = this.store.varInfos;
  /** { [@@varName]: varValue } */
  readonly varValues: Signal<Record<string, any>> = computed(() => this.getVariableValues());

  addVariableDefinition(
    createWfVarRequest: CreateWorkflowVariableRequest,
  ): Observable<WorkflowVariableDefinitionInfo | null> {
    this.setLoading(true);
    return this.workflowVariablesApiService.create(createWfVarRequest).pipe(
      map((varDef) => {
        this.store.addVarDef(varDef);
        return varDef;
      }),
      catchError((err) => {
        this.notificationService.showError(err);
        return of(null);
      }),
      finalize(() => {
        this.setLoading(false);
      }),
    );
  }

  addVariableInfo(variable: VariableInfo): Observable<VariableInfo | null> {
    return this.variablesCollService.add(variable);
  }

  /**
   * Adds to working memory variable infos from server. Use this when vars were already added
   * on the server side and need to be added to our store (for example when workflow execute
   * returns new variables).
   */
  addVariableInfosFromServer(variables: VariableInfo[]): void {
    this.variablesCollService.addManyToCache(variables);
  }

  /**
   * Clears variable definitions and their infos (values) from the store.
   */
  clearVariablesInMemory(): void {
    this.isLoadingCounter.set(0);
    this.store.setVarDefs([]);
    this.store.setVarInfos([]);
    this.variablesCollService.clearCache();
  }

  deleteVariableDefinition(id: string): Observable<boolean> {
    this.setLoading(true);
    return this.workflowVariablesApiService.delete(id).pipe(
      map(() => {
        this.store.deleteVarDef(id);
        return true;
      }),
      catchError((err) => {
        this.notificationService.showError(err);
        return of(false);
      }),
      finalize(() => {
        this.setLoading(false);
      }),
    );
  }

  fetchWorkflowVariables(workflowId: string): Observable<WorkflowVariableDefinitionInfo[]> {
    return this.workflowVariablesApiService.get({ workflowId }).pipe(
      tap((fetchedVarDefs) => {
        for (const fetchedVarDef of fetchedVarDefs) {
          const existingVarDef = this.store.varDefs().find((v) => v.id === fetchedVarDef.id);
          if (existingVarDef) {
            this.store.updateVarDef(fetchedVarDef);
          } else {
            this.store.addVarDef(fetchedVarDef);
          }
        }
      }),
    );
  }

  /**
   * Returns variable info for a given variable name.
   */
  getVariableInfo(varName?: string): VariableInfo | undefined {
    if (!varName) {
      return;
    }
    const varInfos = this.store.varInfos();
    varName = normalizeVarName(varName);
    const varInfo = varInfos.find((info) => info.name === varName);
    return varInfo;
  }

  getVariableValues(useDefault = true): Record<string, any> {
    const varDefs = this.varDefs();
    const varInfos = this.varInfos();
    const varValues: Record<string, any> = {}; // can be any but usually a string value

    // Iterate through variable definitions and assign default values first.
    if (useDefault) {
      for (const varDef of varDefs) {
        if (varDef.defaultValue !== undefined) {
          varValues[varDef.name] = varDef.defaultValue;
        }
      }
    }
    // Once defaults are set, iterate through actual saved values and overwrite defaults where the
    // variable has actual value.
    for (const varInfo of varInfos) {
      varValues[varInfo.name] = varInfo.value;
    }

    return varValues;
  }

  getWorkflowVariables(workflowId: string): WorkflowVariableDefinitionInfo[] {
    return this.varDefs().filter((v) => v.workflowId === workflowId);
  }

  /**
   * Fetches variable definitions and variable infos. Subscribes to variable info change if not
   * already subscribed.
   */
  initVariables(
    projectId: string,
    accountName: string | undefined,
  ): Observable<{ varDefs: WorkflowVariableDefinitionInfo[]; varInfos: VariableInfo[] }> {
    this.clearVariablesInMemory();
    // Make sure to re-subscribe when switching between projects
    if (!this.projectId || projectId !== this.projectId) {
      this.varChangesSub?.unsubscribe();
      this.varChangesSub = undefined;
      this.projectId = projectId;
    }
    if (!this.varChangesSub) {
      this.varChangesSub = this.updateStoreOnVarInfoChanges(projectId).subscribe((varInfos) => {
        this.store.setVarInfos(varInfos);
      });
    }
    return combineLatest({
      varDefs: this.fetchVariableDefinitions(projectId),
      varInfos: this.fetchVariableInfos(projectId, accountName),
    }).pipe(first());
  }

  setLocalVariable(variable: VariableInfo): void {
    const varToStore = { ...variable };
    const localVariables = [
      ...(this.localStorageService.get<VariableInfo[]>(StorageKey.VarLocal) || []),
    ];
    // Check if such variable already exists
    const varIndex = localVariables.findIndex(
      ({ name, scope }) => name === varToStore.name && scope === varToStore.scope,
    );
    if (varIndex >= 0) {
      localVariables[varIndex] = varToStore;
    } else {
      localVariables.push(varToStore);
    }
    this.localStorageService.set(StorageKey.VarLocal, localVariables, false);
    this.localVarsChanged$.next(localVariables);
  }

  updateVariableDefinition(
    varDef: WorkflowVariableDefinitionInfo,
  ): Observable<WorkflowVariableDefinitionInfo | null> {
    if (!varDef.scope) {
      console.error('Unable to update variable definition. Scope not provided', { varDef });
      return of(null);
    }
    const updateRequest: UpdateWorkflowVariableRequest = {
      defaultValue: varDef.defaultValue,
      scope: varDef.scope,
      workflowId: varDef.workflowId,
    };

    this.setLoading(true);
    return this.workflowVariablesApiService.update(varDef.id, updateRequest).pipe(
      tap(() => {
        this.store.updateVarDef(varDef);
      }),
      catchError((err) => {
        this.notificationService.showError(err);
        return of(null);
      }),
      finalize(() => {
        this.setLoading(false);
      }),
    );
  }

  updateVariableInfo(updatedVariable: VariableInfo): Observable<VariableInfo> {
    return this.variablesCollService.update(updatedVariable);
  }

  /**
   * Updates variable infos using new information from the server. Use this when vars were already
   * updated on the server side and need to be updated in our store
   * (for example when workflow execute returns updated variables).
   */
  updateVariableInfosFromServer(variableInfos: VariableInfo[]): void {
    return this.variablesCollService.updateManyInCache(variableInfos);
  }

  private fetchLocalVariablesForProject(projectId: string): VariableInfo[] {
    const localVariables: VariableInfo[] =
      this.localStorageService.get<VariableInfo[]>(StorageKey.VarLocal) || [];
    return localVariables.filter((variable) => variable.projectId === projectId) || [];
  }

  private fetchVariableDefinitions(
    projectId: string,
  ): Observable<WorkflowVariableDefinitionInfo[]> {
    this.setLoading(true);
    return this.workflowVariablesApiService.get({ projectId, includeReferences: true }).pipe(
      catchError((err) => {
        this.notificationService.showError(err);
        return of([] as WorkflowVariableDefinitionInfo[]);
      }),
      tap((variables) => {
        this.store.setVarDefs(variables);
      }),
      finalize(() => {
        this.setLoading(false);
      }),
    );
  }

  private fetchVariableInfos(
    projectId: string,
    accountName: string | undefined,
  ): Observable<VariableInfo[]> {
    this.variablesCollService.clearCache();

    const getVars$ = accountName
      ? this.variablesCollService.getWithQuery({ projectId, scopeValue: accountName })
      : this.variablesCollService.getWithQuery({ projectId });

    return getVars$.pipe(
      first(),
      catchError(() => {
        console.error(`Unable to fetch variables for project ${projectId}`);
        return of([]);
      }),
    );
  }

  private setLoading(isLoading: boolean): void {
    this.isLoadingCounter.update((counter) => (isLoading ? counter + 1 : counter - 1));
  }

  private updateStoreOnVarInfoChanges(projectId: string): Observable<VariableInfo[]> {
    return combineLatest({
      localVars: this.localVarsChanged$.pipe(startWith([] as VariableInfo[])),
      serverVars: this.variablesCollService.entities$.pipe(startWith([] as VariableInfo[])),
    }).pipe(
      distinctUntilChanged(isDeepEqual),
      map(({ serverVars }) => {
        const localVariables = this.fetchLocalVariablesForProject(projectId);
        return serverVars.concat(localVariables);
      }),
    );
  }
}
